import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import APIHelper from './APIHelper';
import { GetActiveCountryCookie } from './Helper';

/**
 * @class API
 * Handles API requests
 */
class API {
  baseUrl = null as string| null;
  internalAxios: AxiosInstance;
  dont_refresh_token = false as boolean;

  constructor(inpBaseURL: string | null) {
    this.baseUrl = inpBaseURL as string;

    this.internalAxios = axios.create({
      baseURL: this.baseUrl,
      headers: {
        Accept: 'application/json'
      }
    }) as AxiosInstance;

    // response interceptor to handle request errors
    this.initialiseResponseInterceptors();

    // response interceptor to handle out of sync countries
    this.initialiseRequestInterceptors();
  }

  initialiseResponseInterceptors() {
    this.internalAxios.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        // return response if success
        // TODO: change to response.data to prevent redundent code
        return response;
      },
      async (error: any) => {
        if (!this.dont_refresh_token && error.response && error.response.status === 401 && error.response.data.error == 'Unauthenticated.') { 
          // Unauthorized - assume token is invalid.  If not trying to get a token then refresh it
          const url = error.response && error.response.request && error.response.request.responseURL;
          if (url.includes('oauth/token')) {
            // invalid refresh token go to login
            APIHelper.handleLogin({ fullPath: window.location.href, query: {} });
            return;
          } else {
            try {
              let token = await APIHelper.refreshToken();
              if (!token) {
                // invalid refresh token go to login
                APIHelper.handleLogin({ fullPath: window.location.href, query: {} });
                return;
              }

              const config = error.config
              config.headers.Authorization = 'Bearer ' + localStorage.getItem("tka")

              //Flag not to refresh token if this request fails, as this can cause an infinite loop
              this.dont_refresh_token = true;
              setTimeout(() => {
                  this.dont_refresh_token = false;
              }, 1000);

              return this.internalAxios.request(config)
            } catch (e) {
              // invalid refresh token go to login
              APIHelper.handleLogin({ fullPath: window.location.href, query: {} });
              return;
            }
          }
        }
        // not an auth error just return
        return Promise.reject(error);
      }
    )
  }

  initialiseRequestInterceptors() {

    this.internalAxios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (config.headers.common['X-as-country-code']) {
          // Config amendment
          if (config.headers.common['X-as-country-code'] !== GetActiveCountryCookie()) {
            //console.log('Country out of sync');
          }
        }
        return config;
      },
      (error) => {
        // Error amendment
        return Promise.reject(error);
    });
  }

  getAxios() : AxiosInstance | null {
    return this.internalAxios;
  }
  setHeaderCommons(headerName: string, headerValue: string | null) {
    if (this.internalAxios)
      this.internalAxios.defaults.headers.common[headerName] = headerValue;
  }

  /**
   * @function get
   * @type async
   * @param {String} endpoint
   * Performs an axios `get` method on the API
   */
  get(endpoint: string, config = {} as AxiosRequestConfig) {
    return this.internalAxios.get(endpoint, config);
  }

  /**
   * @function patch
   * @type async
   * @param {String} endpoint
   * Performs an axios `patch` method on the API
   */
  patch(endpoint: string, data = {} as any, config = {} as AxiosRequestConfig) {
    return this.internalAxios.patch(endpoint, data, config);
  }

  /**
   * @function put
   * @type async
   * @param {String} endpoint
   * Performs an axios `get` method on the API
   */
  put(endpoint: string, data = {} as any, config = {} as AxiosRequestConfig) {
    return this.internalAxios.put(endpoint, data, config);
  }

  /**
   * @function post
   * @type async
   * @param {String} endpoint
   * Performs an axios `post` method on the API
   */
  post(endpoint: string, data = {} as any, config = {} as AxiosRequestConfig) {
    return this.internalAxios.post(endpoint, data, config);
  }

  /**
   * @function delete
   * @type async
   * @param {String} endpoint
   * Performs an axios `delete` method on the API
   */
  delete(endpoint: string, config = {} as AxiosRequestConfig) {
    return this.internalAxios.delete(endpoint, config);
  }

  createCancelToken() : CancelTokenSource {
    return axios.CancelToken.source();
  }
}

export default API;